import React, { useState } from 'react';
import { XMarkIcon, ArrowDownTrayIcon } from '@heroicons/react/24/outline';
import { User } from '../services/api';
import ConfirmModal from './ConfirmModal';

interface UserEditModalProps {
  user: User;
  onSave: (user: User) => void;
  onClose: () => void;
  isLoading?: boolean;
  error?: string | null;
}

const UserEditModal: React.FC<UserEditModalProps> = ({ user, onSave, onClose, isLoading = false, error: propError = null }) => {
  const [formData, setFormData] = useState({
    name: user.name,
    email: user.email,
    phone: user.phone || '',
    isActive: user.isActive,
  });
  const [error, setError] = useState<string | null>(null);
  const [showConfirmModal, setShowConfirmModal] = useState(false);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setError(null); // Clear previous errors
    
    // Check if there are any changes
    const hasChanges = 
      formData.name !== user.name ||
      formData.email !== user.email ||
      formData.phone !== (user.phone || '') ||
      formData.isActive !== user.isActive;
    
    if (!hasChanges) {
      // No changes, just close the modal
      onClose();
      return;
    }
    
    // Show confirmation dialog
    setShowConfirmModal(true);
  };

  const confirmSave = () => {
    // Only send fields that are allowed to be updated
    const updateData = {
      id: user.id, // Keep id for reference
      name: formData.name,
      email: formData.email,
      phone: formData.phone || null,
      isActive: formData.isActive,
    };
    
    setShowConfirmModal(false);
    onSave(updateData as User);
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value,
    }));
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl max-w-md w-full mx-4">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <h2 className="text-lg font-semibold text-gray-900">Edit User</h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600"
          >
            <XMarkIcon className="w-5 h-5" />
          </button>
        </div>

        {/* Content */}
        <form onSubmit={handleSubmit} className="p-6">
          {/* Error Display */}
          {(error || propError) && (
            <div className="bg-red-50 border border-red-200 rounded-md p-4 mb-4">
              <div className="flex">
                <div className="flex-shrink-0">
                  <svg className="h-5 w-5 text-red-400" viewBox="0 0 20 20" fill="currentColor">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                  </svg>
                </div>
                <div className="ml-3">
                  <h3 className="text-sm font-medium text-red-800 text-left">Error</h3>
                  <div className="mt-2 text-sm text-red-700">{error || propError}</div>
                </div>
              </div>
            </div>
          )}
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 text-left">Name</label>
              <input
                type="text"
                name="name"
                value={formData.name}
                onChange={handleChange}
                className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-1 focus:ring-imola-primary focus:border-imola-primary"
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 text-left">Email</label>
              <input
                type="email"
                name="email"
                value={formData.email}
                onChange={handleChange}
                readOnly={true}
                className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-1 focus:ring-imola-primary focus:border-imola-primary"
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 text-left">Phone</label>
              <input
                type="tel"
                name="phone"
                value={formData.phone}
                onChange={handleChange}
                className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-1 focus:ring-imola-primary focus:border-imola-primary"
                placeholder="+1234567890"
              />
            </div>

            <div className="flex items-center">
              <input
                type="checkbox"
                name="isActive"
                checked={formData.isActive}
                onChange={handleChange}
                className="h-4 w-4 text-imola-primary focus:ring-imola-primary border-gray-300 rounded"
              />
              <label className="ml-2 block text-sm text-gray-900">
                Active Account
              </label>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 text-left">Created</label>
              <p className="mt-1 text-sm text-gray-500 text-left">
                {new Date(user.createdAt).toLocaleDateString()} at {new Date(user.createdAt).toLocaleTimeString()}
              </p>
            </div>

            {/* <div>
              <label className="block text-sm font-medium text-gray-700 text-left">Last Login</label>
              <p className="mt-1 text-sm text-gray-500 text-left">
                {user.lastLogin
                  ? `${new Date(user.lastLogin).toLocaleDateString()} at ${new Date(user.lastLogin).toLocaleTimeString()}`
                  : 'Never'
                }
              </p>
            </div> */}
          </div>
        </form>

        {/* Footer */}
        <div className="flex justify-end space-x-3 p-6 border-t border-gray-200">
          <button
            onClick={onClose}
            className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-imola-primary"
          >
            Cancel
          </button>
          <button
            onClick={handleSubmit}
            className="px-4 py-2 text-sm font-medium text-white bg-imola-primary border border-transparent rounded-md hover:bg-imola-primary/80 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-imola-primary flex items-center"
          >
            <ArrowDownTrayIcon className="w-4 h-4 mr-2" />
            Save Changes
          </button>
        </div>
      </div>

      {/* Confirm Modal */}
      <ConfirmModal
        visible={showConfirmModal}
        title="Confirm Update"
        message={`Are you sure you want to update user "${user.name}"?`}
        confirmText="Update"
        cancelText="Cancel"
        onConfirm={confirmSave}
        onCancel={() => setShowConfirmModal(false)}
      />
    </div>
  );
};

export default UserEditModal; 